/**
 * What a calendar entry actually is: time on the clock, or an absence.
 *
 * One table, read by the modal, the three calendars, the payroll tab, the CSV
 * export and the mapper, so a type is described in exactly one place and adding
 * an eighth means editing one array rather than hunting six switch statements.
 *
 * The string values are the wire format. They are SCREAMING_SNAKE because the
 * backend will store them as a Postgres enum and NestJS validates them with
 * `@IsEnum`, see `../config.ts` for the contract.
 */

export type ShiftType =
  | 'WORK'
  | 'ON_CALL'
  | 'TRAINING'
  | 'TIME_OFF'
  | 'VACATION'
  | 'SICK_LEAVE'
  | 'UNAVAILABLE'

export interface ShiftTypeDefinition {
  type: ShiftType
  /** Key present in both en.json and pl.json. */
  labelKey: string
  /**
   * Covers the whole day, so the modal hides the clocks.
   *
   * Stored as 00:00-24:00 rather than as null times on purpose: every calendar,
   * the overlap maths, the drag handling and the export already assume a shift
   * has a start and an end, and making those optional would put a branch in all
   * of them for a case the user never types. The flag is what the UI reads; the
   * geometry stays one shape.
   */
  allDay: boolean
  /**
   * Time the employee is on the clock, so it feeds the hours and the money in
   * the payroll tab. An absence is still totalled, but in days, and never
   * multiplied by a rate.
   */
  paid: boolean
}

export const SHIFT_TYPES: ShiftTypeDefinition[] = [
  { type: 'WORK', labelKey: 'SCHEDULE.TYPE.WORK', allDay: false, paid: true },
  { type: 'ON_CALL', labelKey: 'SCHEDULE.TYPE.ON_CALL', allDay: false, paid: true },
  { type: 'TRAINING', labelKey: 'SCHEDULE.TYPE.TRAINING', allDay: false, paid: true },
  { type: 'TIME_OFF', labelKey: 'SCHEDULE.TYPE.TIME_OFF', allDay: true, paid: false },
  { type: 'VACATION', labelKey: 'SCHEDULE.TYPE.VACATION', allDay: true, paid: false },
  { type: 'SICK_LEAVE', labelKey: 'SCHEDULE.TYPE.SICK_LEAVE', allDay: true, paid: false },
  {
    type: 'UNAVAILABLE',
    labelKey: 'SCHEDULE.TYPE.UNAVAILABLE',
    allDay: true,
    paid: false
  }
]

/**
 * What an entry is when nothing says otherwise.
 *
 * Also what every shift saved before this feature existed is read as, both from
 * the API and from the persisted store, so no roster needs migrating.
 */
export const DEFAULT_SHIFT_TYPE: ShiftType = 'WORK'

const byType = new Map<ShiftType, ShiftTypeDefinition>(
  SHIFT_TYPES.map(definition => [definition.type, definition])
)

/** Never throws: an unknown value from an older client reads as work. */
export const shiftTypeDefinition = (
  type: ShiftType | undefined
): ShiftTypeDefinition =>
  (type && byType.get(type)) || byType.get(DEFAULT_SHIFT_TYPE)!

export const isAllDayType = (type: ShiftType | undefined): boolean =>
  shiftTypeDefinition(type).allDay

export const isPaidType = (type: ShiftType | undefined): boolean =>
  shiftTypeDefinition(type).paid

/** Guard for values crossing the wire or coming out of localStorage. */
export const isShiftType = (value: unknown): value is ShiftType =>
  typeof value === 'string' && byType.has(value as ShiftType)
